<template>
  <div class="forum-wrapper">
    <div class="forum-layout">
      <!-- Main Column -->
      <div class="main-column">
        <!-- User Activity Header -->
        <header class="activity-header">
          <h1 class="typo-headline-4">Mi Actividad en el Foro</h1>
        </header>

        <!-- Tabs for different activity types -->
        <div class="activity-tabs">
          <button 
            class="tab-button typo-button" 
            :class="{ active: activeTab === 'threads' }"
            @click="activeTab = 'threads'"
          >
            Mis Hilos <span class="count">({{ threadsPagination.total || 0 }})</span>
          </button>
          <button 
            class="tab-button typo-button" 
            :class="{ active: activeTab === 'comments' }"
            @click="activeTab = 'comments'"
          >
            Mis Comentarios <span class="count">({{ commentsPagination.total || 0 }})</span>
          </button>
          <button 
            class="tab-button typo-button" 
            :class="{ active: activeTab === 'favorites' }"
            @click="activeTab = 'favorites'"
          >
            Favoritos <span class="count">({{ favoritesPagination.total || 0 }})</span>
          </button>
          <button 
            class="tab-button typo-button" 
            :class="{ active: activeTab === 'notifications' }"
            @click="setActiveTab('notifications')"
          >
            Notificaciones <span class="count">({{ totalNotificationTabCount }})</span>
            <span v-if="notificationCount > 0" class="notification-dot"></span>
          </button>
        </div>

        <!-- Loading and Error States -->
        <div v-if="loading" class="loading-message typo-body-2">
          Cargando tu actividad...
        </div>
        <div v-else-if="error" class="error-message typo-body-2">
          {{ error }}
        </div>
        <div v-else>
          <!-- Threads Tab Content -->
          <div v-if="activeTab === 'threads'" class="tab-content">
            <div v-if="threads.length === 0" class="empty-state">
              <p class="typo-body-1">No has creado ningún hilo todavía.</p>
              <button class="create-thread-btn typo-button" @click="createNewThread">
                <i class="material-icons">add</i>
                Crear Nuevo Hilo
              </button>
            </div>
            <div v-else class="threads-list">
              <router-link 
                v-for="thread in threads" 
                :key="thread.id" 
                :to="{ name: 'ForumThread', params: { nid: thread.id } }"
                class="thread-item clickable-thread"
              >
                <div class="thread-main">
                  <h3 class="thread-title typo-subtitle-1">
                    {{ thread.title }}
                  </h3>
                  <div class="thread-meta typo-caption">
                    <span class="thread-date">{{ formatDate(thread.created) }}</span>
                    <span class="separator">•</span>
                    <span class="comment-count">{{ thread.commentCount }} comentarios</span>
                  </div>
                </div>
              </router-link>
              
              <!-- Pagination for threads -->
              <div class="pagination" v-if="threadsPagination.pages > 1">
                <button 
                  class="pagination-btn" 
                  :disabled="threadsPage === 0" 
                  @click="prevThreadsPage"
                >
                  Anterior
                </button>
                <span class="page-info">
                  Página {{ threadsPage + 1 }} de {{ threadsPagination.pages }}
                </span>
                <button 
                  class="pagination-btn" 
                  :disabled="threadsPage >= threadsPagination.pages - 1" 
                  @click="nextThreadsPage"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>

          <!-- Comments Tab Content -->
          <div v-if="activeTab === 'comments'" class="tab-content">
            <div v-if="comments.length === 0" class="empty-state">
              <p class="typo-body-1">No has publicado ningún comentario todavía.</p>
            </div>
            <div v-else class="comments-list">
              <router-link 
                v-for="comment in comments" 
                :key="comment.id" 
                :to="'/app' + comment.url"
                class="comment-item clickable-comment"
              >
                <div class="comment-main">
                  <h3 class="comment-thread-title typo-subtitle-1">
                    {{ comment.threadTitle }}
                  </h3>
                  <div class="comment-content typo-body-2">
                    <p v-if="comment.subject" class="comment-subject">{{ comment.subject }}</p>
                    <p class="comment-body">{{ comment.body }}</p>
                  </div>
                  <div class="comment-meta typo-caption">
                    <span class="comment-date">{{ formatDate(comment.created) }}</span>
                  </div>
                </div>
              </router-link>
              
              <!-- Pagination for comments -->
              <div class="pagination" v-if="commentsPagination.pages > 1">
                <button 
                  class="pagination-btn" 
                  :disabled="commentsPage === 0" 
                  @click="prevCommentsPage"
                >
                  Anterior
                </button>
                <span class="page-info">
                  Página {{ commentsPage + 1 }} de {{ commentsPagination.pages }}
                </span>
                <button 
                  class="pagination-btn" 
                  :disabled="commentsPage >= commentsPagination.pages - 1" 
                  @click="nextCommentsPage"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>

          <!-- Favorites Tab Content -->
          <div v-if="activeTab === 'favorites'" class="tab-content">
            <div v-if="favorites.length === 0" class="empty-state">
              <p class="typo-body-1">No has marcado ningún hilo como favorito todavía.</p>
              <p class="typo-body-2">Puedes marcar hilos como favoritos para acceder a ellos fácilmente más tarde.</p>
            </div>
            <div v-else class="threads-list">
              <router-link 
                v-for="thread in favorites" 
                :key="thread.id" 
                :to="{ name: 'ForumThread', params: { nid: thread.id } }"
                class="thread-item clickable-thread"
              >
                <div class="thread-main">
                  <h3 class="thread-title typo-subtitle-1">
                    {{ thread.title }}
                  </h3>
                  <div class="thread-meta typo-caption">
                    <span class="thread-author">Por: {{ thread.author }}</span>
                    <span class="separator">•</span>
                    <span class="thread-date">{{ formatDate(thread.created) }}</span>
                    <span class="separator">•</span>
                    <span class="comment-count">{{ thread.commentCount }} comentarios</span>
                  </div>
                </div>
              </router-link>
              
              <!-- Pagination for favorites -->
              <div class="pagination" v-if="favoritesPagination.pages > 1">
                <button 
                  class="pagination-btn" 
                  :disabled="favoritesPage === 0" 
                  @click="prevFavoritesPage"
                >
                  Anterior
                </button>
                <span class="page-info">
                  Página {{ favoritesPage + 1 }} de {{ favoritesPagination.pages }}
                </span>
                <button 
                  class="pagination-btn" 
                  :disabled="favoritesPage >= favoritesPagination.pages - 1" 
                  @click="nextFavoritesPage"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>

          <!-- Notifications Tab Content -->
          <div v-if="activeTab === 'notifications'" class="tab-content">
            <!-- Settings Section (always visible) -->
            <div class="notification-settings">
              <h4 class="settings-title typo-subtitle-2">Configuración de Notificaciones</h4>
              <div class="setting-item">
                <label class="setting-label typo-body-2">
                  <input 
                    type="checkbox" 
                    v-model="showForumNotificationsInMenu"
                    @change="saveForumNotificationSetting"
                    class="setting-checkbox"
                  />
                  Mostrar notificaciones del foro en el menú principal
                </label>
                <p class="setting-description typo-caption">
                  Cuando está activado, verás un punto rojo en el icono del foro en el menú lateral cuando tengas notificaciones sin leer.
                </p>
              </div>
            </div>
            
            <div v-if="threadResponses.length === 0 && commentResponses.length === 0" class="empty-state">
              <p class="typo-body-1">No tienes notificaciones nuevas.</p>
            </div>
            <div v-else class="notifications-content">
              <!-- Action Buttons -->
              <div class="notifications-header">
                <button 
                  class="mark-all-read-btn typo-button" 
                  @click="markAllAsRead"
                  :disabled="markingAllAsRead"
                >
                  <i class="material-icons">mark_email_read</i>
                  {{ markingAllAsRead ? 'Marcando...' : 'Marcar todas como leídas' }}
                </button>
                <button 
                  class="clear-read-btn typo-button" 
                  @click="clearReadNotifications"
                  :disabled="clearingRead"
                  v-if="hasReadNotifications"
                >
                  <i class="material-icons">clear_all</i>
                  {{ clearingRead ? 'Limpiando...' : 'Limpiar leídas' }}
                </button>
              </div>
              <!-- Thread Responses -->
              <div v-if="threadResponses.length > 0" class="notification-section">
                <h3 class="section-title typo-subtitle-1">Respuestas a tus hilos</h3>
                <div class="notification-list">
                  <div 
                    v-for="notification in threadResponses" 
                    :key="notification.comment_id"
                    class="notification-wrapper"
                  >
                    <router-link 
                      :to="{ name: 'ForumThread', params: { nid: notification.thread_id }, hash: '#comment-' + notification.comment_id }"
                      target="_blank"
                      class="thread-item clickable-thread notification-thread"
                      :class="{ 'silenced': notification.silenced }"
                      @click.native="markSingleAsRead(notification.thread_id, notification.comment_id)"
                    >
                      <div class="thread-main">
                        <h3 class="thread-title typo-subtitle-1">
                          {{ notification.thread_title }}
                        </h3>
                        <p class="notification-content typo-body-2">
                          <strong>{{ notification.author }}</strong> comentó: {{ notification.comment_text }}
                        </p>
                        <div class="thread-meta typo-caption">
                          <span class="thread-date">{{ formatDate(notification.timestamp) }}</span>
                          <span class="separator">•</span>
                          <span class="click-hint">Haz clic para ver y marcar como leída</span>
                        </div>
                      </div>
                    </router-link>
                    <button 
                      v-if="!notification.silenced"
                      class="mark-read-btn typo-caption"
                      @click="markSingleAsRead(notification.thread_id, notification.comment_id)"
                    >
                      Marcar como leída
                    </button>
                  </div>
                </div>
              </div>

              <!-- Comment Responses -->
              <div v-if="commentResponses.length > 0" class="notification-section">
                <h3 class="section-title typo-subtitle-1">Respuestas a tus comentarios</h3>
                <div class="notification-list">
                  <div 
                    v-for="notification in commentResponses" 
                    :key="notification.comment_id"
                    class="notification-wrapper"
                  >
                    <router-link 
                      :to="{ name: 'ForumThread', params: { nid: notification.thread_id }, hash: '#comment-' + notification.comment_id }"
                      target="_blank"
                      class="comment-item clickable-comment notification-comment"
                      :class="{ 'silenced': notification.silenced }"
                      @click.native="markSingleAsRead(notification.thread_id, notification.comment_id)"
                    >
                      <div class="comment-main">
                        <h3 class="comment-thread-title typo-subtitle-1">
                          {{ notification.thread_title }}
                        </h3>
                        <div class="comment-content typo-body-2">
                          <p class="comment-body">
                            <strong>{{ notification.author }}</strong> respondió a tu comentario: {{ notification.comment_text }}
                          </p>
                        </div>
                        <div class="comment-meta typo-caption">
                          <span class="comment-date">{{ formatDate(notification.timestamp) }}</span>
                          <span class="separator">•</span>
                          <span class="click-hint">Haz clic para ver y marcar como leída</span>
                        </div>
                      </div>
                    </router-link>
                    <button 
                      v-if="!notification.silenced"
                      class="mark-read-btn typo-caption"
                      @click="markSingleAsRead(notification.thread_id, notification.comment_id)"
                    >
                      Marcar como leída
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Side Column -->
      <div class="side-column">
        <!-- Navigation Links -->
        <navigation-links />
        
        <!-- User Activity Widget -->
        <user-activity-widget />
        
        <!-- Recent Content -->
        <recent-content-container />
        
        <!-- Trending Topics -->
        <trending-topics-container />
      </div>
    </div>
  </div>
</template>

<script>
import forumApi from '@/services/forumApi';
import { mapGetters } from 'vuex';
import NavigationLinks from '../components/NavigationLinks.vue';
import UserActivityWidget from '../components/UserActivityWidget.vue';
import RecentContentContainer from '../components/RecentContentContainer.vue';
import TrendingTopicsContainer from '../components/TrendingTopicsContainer.vue';

const axios = forumApi;

export default {
  name: 'UserActivity',
  components: {
    NavigationLinks,
    UserActivityWidget,
    RecentContentContainer,
    TrendingTopicsContainer
  },
  data() {
    return {
      activeTab: 'threads',
      threads: [],
      comments: [],
      favorites: [],
      threadResponses: [],
      commentResponses: [],
      loading: true,
      error: null,
      threadsPage: 0,
      commentsPage: 0,
      favoritesPage: 0,
      itemsPerPage: 10,
      clearingAll: false,
      silencingAll: false,
      markingAllAsRead: false,
      clearingRead: false,
      showForumNotificationsInMenu: true,
      unsilencedNotificationCount: 0,
      totalNotificationCount: 0,
      threadsPagination: {
        total: 0,
        pages: 0
      },
      commentsPagination: {
        total: 0,
        pages: 0
      },
      favoritesPagination: {
        total: 0,
        pages: 0
      }
    };
  },
  computed: {
    ...mapGetters(['_g_IsUserLogged']),
    isLoggedIn() {
      return this._g_IsUserLogged;
    },
    notificationCount() {
      return this.unsilencedNotificationCount;
    },
    totalNotificationTabCount() {
      return this.totalNotificationCount;
    },
    hasReadNotifications() {
      // Check if there are any read (silenced) notifications
      const readThreadNotifications = this.threadResponses.filter(n => n.silenced).length;
      const readCommentNotifications = this.commentResponses.filter(n => n.silenced).length;
      return readThreadNotifications + readCommentNotifications > 0;
    }
  },
  watch: {
    activeTab(newTab) {
      // When tab changes, update URL query parameter
      this.$router.replace({ 
        query: { ...this.$route.query, tab: newTab } 
      });
    }
  },
  mounted() {
    // Check if user is logged in
    if (!this.isLoggedIn) {
      this.$router.push('/forum');
      return;
    }
    
    // Set active tab from URL if present
    if (this.$route.query.tab && ['threads', 'comments', 'favorites', 'notifications'].includes(this.$route.query.tab)) {
      this.activeTab = this.$route.query.tab;
    }
    
    // Load forum notification setting
    this.loadForumNotificationSetting();
    
    // Load initial data
    this.fetchUserActivity();
    
    // Listen for tab change events from UserActivityWidget
    this.$root.$on('changeUserActivityTab', this.handleTabChange);
  },
  beforeDestroy() {
    // Clean up event listeners
    this.$root.$off('changeUserActivityTab', this.handleTabChange);
  },
  methods: {
    fetchUserActivity() {
      this.loading = true;
      this.error = null;
      
      // Create an array of promises for parallel requests
      const promises = [
        this.fetchThreads(),
        this.fetchComments(),
        this.fetchFavorites(),
        this.fetchNotifications()
      ];
      
      // Wait for all requests to complete
      Promise.all(promises)
        .then(() => {
          this.loading = false;
        })
        .catch(err => {
          console.error('Error fetching user activity:', err);
          this.error = 'Error al cargar tu actividad';
          this.loading = false;
        });
    },
    
    fetchThreads() {
      return axios.get(`/forum/api/user/threads?page=${this.threadsPage}&limit=${this.itemsPerPage}`)
        .then(response => {
          if (response.data) {
            this.threads = response.data.threads || [];
            this.threadsPagination = response.data.pagination || { total: 0, pages: 0 };
          }
        });
    },
    
    fetchComments() {
      return axios.get(`/forum/api/user/comments?page=${this.commentsPage}&limit=${this.itemsPerPage}`)
        .then(response => {
          if (response.data) {
            this.comments = response.data.comments || [];
            this.commentsPagination = response.data.pagination || { total: 0, pages: 0 };
          }
        });
    },
    
    fetchFavorites() {
      return axios.get(`/forum/api/user/favorites?page=${this.favoritesPage}&limit=${this.itemsPerPage}`)
        .then(response => {
          if (response.data && response.data.threads) {
            this.favorites = response.data.threads;
            this.favoritesPagination = response.data.pagination;
          } else {
            throw new Error('Invalid response format');
          }
        })
        .catch(err => {
          console.error('Error fetching favorites:', err);
          this.error = 'Error al cargar tus hilos favoritos';
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    fetchNotifications() {
      return axios.get('/forum/api/notifications')
        .then(response => {
          if (response.data) {
            this.threadResponses = response.data.thread_responses || [];
            this.commentResponses = response.data.comment_responses || [];
            this.unsilencedNotificationCount = response.data.unsilenced_count || 0;
            this.totalNotificationCount = response.data.total_count || 0;
          }
        })
        .catch(err => {
          console.error('Error fetching notifications:', err);
          this.threadResponses = [];
          this.commentResponses = [];
          this.unsilencedNotificationCount = 0;
          this.totalNotificationCount = 0;
        });
    },
    
    clearAllNotifications() {
      this.clearingAll = true;
      return axios.post('/eurekers/vdb/clear-forum-notifications')
        .then(() => {
          this.threadResponses = [];
          this.commentResponses = [];
          this.unsilencedNotificationCount = 0;
          this.totalNotificationCount = 0;
          // Emit event to update notification count in widget
          this.$root.$emit('userActivityUpdated', 'notification');
        })
        .catch(err => {
          console.error('Error clearing notifications:', err);
        })
        .finally(() => {
          this.clearingAll = false;
        });
    },
    
    clearSingleNotification(threadId, commentId) {
      const formData = new FormData();
      formData.append('thread_id', threadId);
      formData.append('comment_id', commentId);
      
      return axios.post('/eurekers/vdb/clear-single-forum-notification', formData)
        .then(() => {
          // Find and remove notification from local arrays, update count if it was unsilenced
          const threadNotification = this.threadResponses.find(n => n.comment_id === commentId);
          const commentNotification = this.commentResponses.find(n => n.comment_id === commentId);
          
          if (threadNotification) {
            if (!threadNotification.silenced) {
              this.unsilencedNotificationCount = Math.max(0, this.unsilencedNotificationCount - 1);
            }
            this.totalNotificationCount = Math.max(0, this.totalNotificationCount - 1);
          }
          if (commentNotification) {
            if (!commentNotification.silenced) {
              this.unsilencedNotificationCount = Math.max(0, this.unsilencedNotificationCount - 1);
            }
            this.totalNotificationCount = Math.max(0, this.totalNotificationCount - 1);
          }
          
          // Remove notification from local arrays
          this.threadResponses = this.threadResponses.filter(
            notification => notification.comment_id !== commentId
          );
          this.commentResponses = this.commentResponses.filter(
            notification => notification.comment_id !== commentId
          );
          
          // Emit event to update notification count in widget
          this.$root.$emit('userActivityUpdated', 'notification');
        })
        .catch(err => {
          console.error('Error clearing single notification:', err);
        });
    },
    
    
    markSingleAsRead(threadId, commentId) {
      const formData = new FormData();
      formData.append('thread_id', threadId);
      formData.append('comment_id', commentId);
      
      return axios.post('/eurekers/vdb/silence-single-forum-notification', formData)
        .then(() => {
          // Find and update notification in local arrays
          const threadNotification = this.threadResponses.find(n => n.comment_id === commentId);
          const commentNotification = this.commentResponses.find(n => n.comment_id === commentId);
          
          if (threadNotification && !threadNotification.silenced) {
            threadNotification.silenced = true;
            this.unsilencedNotificationCount = Math.max(0, this.unsilencedNotificationCount - 1);
          }
          if (commentNotification && !commentNotification.silenced) {
            commentNotification.silenced = true;
            this.unsilencedNotificationCount = Math.max(0, this.unsilencedNotificationCount - 1);
          }
          
          // Total count stays the same since we're not deleting, just marking as read
          // Emit event to update notification count in widget
          this.$root.$emit('userActivityUpdated', 'notification');
        })
        .catch(err => {
          console.error('Error marking single notification as read:', err);
        });
    },
    
    markAllAsRead() {
      this.markingAllAsRead = true;
      return axios.post('/eurekers/vdb/silence-all-forum-notifications', new FormData())
        .then(() => {
          // Mark all notifications as read locally
          this.threadResponses = this.threadResponses.map(notification => ({
            ...notification,
            silenced: true
          }));
          this.commentResponses = this.commentResponses.map(notification => ({
            ...notification,
            silenced: true
          }));
          this.unsilencedNotificationCount = 0;
          // Total count remains the same since we're not deleting, just marking as read
          // Emit event to update notification count in widget
          this.$root.$emit('userActivityUpdated', 'notification');
        })
        .catch(err => {
          console.error('Error marking all notifications as read:', err);
        })
        .finally(() => {
          this.markingAllAsRead = false;
        });
    },
    
    clearReadNotifications() {
      this.clearingRead = true;
      return axios.post('/eurekers/vdb/clear-read-forum-notifications', new FormData())
        .then(() => {
          // Remove read notifications from local arrays
          this.threadResponses = this.threadResponses.filter(n => !n.silenced);
          this.commentResponses = this.commentResponses.filter(n => !n.silenced);
          
          // Update total count (unsilenced count should remain the same)
          this.totalNotificationCount = this.threadResponses.length + this.commentResponses.length;
          
          // Emit event to update notification count in widget
          this.$root.$emit('userActivityUpdated', 'notification');
        })
        .catch(err => {
          console.error('Error clearing read notifications:', err);
        })
        .finally(() => {
          this.clearingRead = false;
        });
    },
    
    nextThreadsPage() {
      if (this.threadsPage < this.threadsPagination.pages - 1) {
        this.threadsPage++;
        this.fetchThreads();
      }
    },
    
    prevThreadsPage() {
      if (this.threadsPage > 0) {
        this.threadsPage--;
        this.fetchThreads();
      }
    },
    
    nextCommentsPage() {
      if (this.commentsPage < this.commentsPagination.pages - 1) {
        this.commentsPage++;
        this.fetchComments();
      }
    },
    
    prevCommentsPage() {
      if (this.commentsPage > 0) {
        this.commentsPage--;
        this.fetchComments();
      }
    },
    
    nextFavoritesPage() {
      if (this.favoritesPage < this.favoritesPagination.pages - 1) {
        this.favoritesPage++;
        this.fetchFavorites();
      }
    },
    
    prevFavoritesPage() {
      if (this.favoritesPage > 0) {
        this.favoritesPage--;
        this.fetchFavorites();
      }
    },
    
    formatDate(timestamp) {
      const date = new Date(timestamp * 1000);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    createNewThread() {
      this.$router.push({ name: 'ForumNewThread' });
    },
    
    setActiveTab(newTab) {
      // Handle tab change
      this.activeTab = newTab;
    },
    
    handleTabChange(newTab) {
      // Change the active tab when event is received from UserActivityWidget
      if (['threads', 'comments', 'favorites', 'notifications'].includes(newTab) && newTab !== this.activeTab) {
        this.setActiveTab(newTab);
      }
    },
    
    // Forum notification setting methods
    loadForumNotificationSetting() {
      const saved = localStorage.getItem('show_forum_notifications_menu');
      this.showForumNotificationsInMenu = saved === null ? true : saved === 'true';
    },
    
    saveForumNotificationSetting() {
      localStorage.setItem('show_forum_notifications_menu', this.showForumNotificationsInMenu.toString());
      // Emit event to Menu component
      this.$root.$emit('forumNotificationSettingChanged', this.showForumNotificationsInMenu);
    }
  }
};
</script>

<style scoped>
.forum-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.forum-layout {
  display: flex;
  gap: 20px;
}

.main-column {
  flex: 1;
  min-width: 0; /* Prevents flex items from overflowing */
}

.side-column {
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.activity-header {
  color: var(--color-text-primary);
  margin-bottom: 20px;
}

.activity-tabs {
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--color-outline);
}

.tab-button {
  padding: 12px 20px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
}

.tab-button.active {
  color: var(--color-highlight);
  border-bottom-color: var(--color-highlight);
}

.count {
  color: var(--color-text-secondary);
  font-size: 0.9em;
}

.tab-content {
  background-color: var(--color-surface);
  border-radius: 8px;
  padding: 20px;
}

.empty-state {
  text-align: center;
  padding: 40px 0;
  color: var(--color-text-primary);
}

.empty-state p {
  color: var(--color-text-primary);
}

.empty-state .typo-body-2 {
  color: var(--color-text-secondary);
}

.create-thread-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  margin: 20px auto 0;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.create-thread-btn:hover {
  background-color: var(--color-highlight-dark, #1976d2);
}

.thread-item, .comment-item {
  padding: 15px;
  border-bottom: 1px solid var(--color-outline);
}

.thread-item:last-child, .comment-item:last-child {
  border-bottom: none;
}

.clickable-thread, .clickable-comment {
  display: block;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.2s ease, transform 0.1s ease;
  border-radius: 4px;
  cursor: pointer;
}

.clickable-thread:hover, .clickable-comment:hover {
  background-color: var(--color-active-surface, rgba(0, 0, 0, 0.04));
  text-decoration: none;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.clickable-thread .thread-title, .clickable-comment .comment-thread-title {
  color: var(--color-highlight);
  margin: 0;
}

.clickable-thread:hover .thread-title, .clickable-comment:hover .comment-thread-title {
  color: var(--color-highlight-dark, #1976d2);
}

.thread-meta, .comment-meta {
  display: flex;
  align-items: center;
  color: var(--color-text-secondary);
  margin-top: 5px;
}

.separator {
  margin: 0 8px;
}

.comment-content {
  margin: 10px 0;
}

.comment-subject {
  font-weight: 500;
  margin-bottom: 5px;
}

.comment-body {
  color: var(--color-text-primary);
  margin: 0;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 15px;
}

.pagination-btn {
  background-color: var(--color-background);
  color: var(--color-text-primary);
  border: 1px solid var(--color-outline);
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.pagination-btn:hover:not(:disabled) {
  background-color: var(--color-active-surface);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  color: var(--color-text-secondary);
}

.loading-message,
.error-message {
  padding: 20px;
  background-color: var(--color-surface);
  border-radius: 8px;
  text-align: center;
  margin: 20px 0;
  color: var(--color-text-primary);
}

.error-message {
  border-left: 4px solid #f44336;
  color: #f44336;
}

.notification-dot {
  width: 8px;
  height: 8px;
  background-color: #f44336;
  border-radius: 50%;
  display: inline-block;
  margin-left: 5px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

.notifications-content {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.notification-section {
  border-bottom: 1px solid var(--color-outline);
  padding-bottom: 20px;
}

.notification-section:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.section-title {
  color: var(--color-text-primary);
  margin-bottom: 15px;
  font-weight: 600;
}

.notification-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.notification-item {
  background-color: var(--color-surface);
  border-radius: 8px;
  padding: 15px;
  border-left: 4px solid var(--color-highlight);
  transition: all 0.2s ease;
  border: 1px solid var(--color-outline);
}

.notification-item:hover {
  background-color: var(--color-active-surface);
}

.clickable-notification {
  cursor: pointer;
}

.clickable-notification:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-color: var(--color-highlight);
}

.notification-thread-title {
  margin: 0 0 10px 0;
}

.notification-thread-title a {
  color: var(--color-highlight);
  text-decoration: none;
  font-weight: 600;
}

.notification-thread-title a:hover {
  text-decoration: underline;
}

.notification-content {
  color: var(--color-text-primary);
  margin-bottom: 10px;
  line-height: 1.4;
}

.notification-meta {
  display: flex;
  align-items: center;
  color: var(--color-text-secondary);
}

.view-comment-link {
  color: var(--color-highlight);
  text-decoration: none;
  font-weight: 500;
}

.view-comment-link:hover {
  text-decoration: underline;
}

.notifications-header {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid var(--color-outline);
}

.clear-all-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: var(--color-error, #f44336);
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  font-weight: 500;
}

.clear-all-btn:hover:not(:disabled) {
  background-color: var(--color-error-dark, #d32f2f);
}

.clear-all-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.clear-all-btn .material-icons {
  font-size: 18px;
}

.silence-all-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: var(--color-warning, #ff9800);
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  font-weight: 500;
}

.silence-all-btn:hover:not(:disabled) {
  background-color: var(--color-warning-dark, #f57c00);
}

.silence-all-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.silence-all-btn .material-icons {
  font-size: 18px;
}

.notification-thread,
.notification-comment {
  position: relative;
}

.notification-thread.silenced,
.notification-comment.silenced {
  opacity: 0.7;
}

.notification-thread.silenced .thread-title,
.notification-thread.silenced .notification-content,
.notification-comment.silenced .comment-thread-title,
.notification-comment.silenced .comment-body {
  color: var(--color-text-secondary);
}

.mark-read-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: var(--color-highlight, #2196f3);
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 12px;
  transition: all 0.2s ease;
  font-size: 11px;
  font-weight: 500;
  opacity: 0.9;
  white-space: nowrap;
}

.mark-read-btn:hover {
  opacity: 1;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.mark-all-read-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: var(--color-highlight, #2196f3);
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  font-weight: 500;
}

.mark-all-read-btn:hover:not(:disabled) {
  background-color: var(--color-highlight-dark, #1976d2);
}

.mark-all-read-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.mark-all-read-btn .material-icons {
  font-size: 18px;
}

.clear-read-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: var(--color-error, #f44336);
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  font-weight: 500;
}

.clear-read-btn:hover:not(:disabled) {
  background-color: var(--color-error-dark, #d32f2f);
}

.clear-read-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.clear-read-btn .material-icons {
  font-size: 18px;
}

.click-hint {
  color: var(--color-text-secondary);
  font-style: italic;
}

/* Notification Settings Styles */
.notification-settings {
  background-color: var(--color-background, #f5f5f5);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  border-left: 4px solid var(--color-highlight);
}

.settings-title {
  color: var(--color-text-primary);
  margin: 0 0 15px 0;
  font-weight: 600;
}

.setting-item {
  margin-bottom: 15px;
}

.setting-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: var(--color-text-primary);
  cursor: pointer;
  line-height: 1.4;
}

.setting-checkbox {
  margin-top: 2px;
  accent-color: var(--color-highlight);
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.setting-description {
  color: var(--color-text-secondary);
  margin: 8px 0 0 26px;
  line-height: 1.3;
}

/* Responsive Design */
@media (max-width: 768px) {
  .forum-layout {
    flex-direction: column;
  }
  
  .main-column,
  .side-column {
    width: 100%;
  }
  
  .side-column {
    margin-top: 20px;
  }
}
</style> 